// tabs/weapon-abilities.ts — the weapons_equipped ATTACK + OPPORTUNITY-ATTACK ability tables,
// rendered client-side from the caster blob. Client port of the two render_abilities() calls in
// options/game/character_sheet_display/weapons_equipped/_weapons_equipped_inc.php (PHP
// render_abilities() in includes/functions/encounter_creatures/render_abilities_inc.php).
//
// Rows reuse the shared buildSection/buildRow (ability-row.ts) so they're identical to the
// Abilities tab. The attack section header carries the class-mechanic charge banner (port of
// ft_class_mechanic_attack_header_suffix_html) showing each class mechanic's current/max charges
// + ±1 buttons.

import type { CachedCaster } from '../types';
import type { CachedCaster as InfoPanelCaster } from '../../info-panel/types';
import { num } from '../read-helpers';
import {
    buildSection,
    iconPh,
    uuidOf,
    abilityEntryId,
    abilityEntryName,
    abilityEntryCharges,
    abilityEntryMaxCharges,
    passiveActive,
    abilityEntriesOfType,
} from './ability-row';
import { casterIsInCombat } from './movement';
import { setActiveInfoPanel, paintActiveInfoPanel, rerenderActiveInfoPanel } from '../../info-panel/active-panel';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';

type WeaponAbilitiesWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    adjustCharge?: (abilityId: string, delta: string, refresh: boolean) => unknown;
    changeCharges?: (abilityId: string, inputId: string) => unknown;
    ftChargeActorRow?: (actorId: string) => CachedCaster | null;
    adjustShamanMoteToggle?: (abilityId: string, current: number, max: number, element: HTMLElement) => unknown;
    forceOpenSideInfoPanel?: () => unknown;
    renderClassMechanicInfoPanel?: (casterId: string, mechanicId: string) => unknown;
    ftActionBarPivotMechanic?: (casterId: string, delta: number) => unknown;
};

function weaponAbilityAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-weapon-ability-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function weaponAbilityInfoAttrs(itemId: string): string {
    return weaponAbilityAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-weapon-ability-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftWeaponAbilityAction ?? '';
    const w = window as WeaponAbilitiesWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'adjust-charge') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const delta = el.dataset.ftDelta ?? '0';
        if (abilityId !== '') { event.preventDefault(); w.adjustCharge?.(abilityId, delta, true); }
    } else if (action === 'shaman-mote-toggle') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const current = Number(el.dataset.ftCurrent ?? '0');
        const max = Number(el.dataset.ftMax ?? '1');
        if (abilityId !== '') { event.preventDefault(); w.adjustShamanMoteToggle?.(abilityId, current, max, el); }
    } else if (action === 'class-mechanic-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderClassMechanicInfoPanel?.(el.dataset.ftCasterId ?? '', el.dataset.ftMechanicId ?? '');
    } else if (action === 'pivot-mechanic') {
        event.preventDefault();
        w.ftActionBarPivotMechanic?.(el.dataset.ftCasterId ?? '', Number(el.dataset.ftDelta ?? '0'));
    }
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-weapon-ability-action="shaman-mote-toggle"]')
        : null;
    if (!el || !document.contains(el)) return;
    event.preventDefault();
    const w = window as WeaponAbilitiesWindow;
    w.adjustShamanMoteToggle?.(el.dataset.ftAbilityId ?? '', Number(el.dataset.ftCurrent ?? '0'), Number(el.dataset.ftMax ?? '1'), el);
});

document.addEventListener('input', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLInputElement>('[data-ft-weapon-ability-action="mechanic-slider"]')
        : null;
    if (!el || !document.contains(el)) return;
    const labelId = el.dataset.ftLabelId ?? '';
    const label = labelId !== '' ? document.getElementById(labelId) : null;
    if (label) label.textContent = el.value;
});

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLInputElement>('[data-ft-weapon-ability-action="mechanic-slider"]')
        : null;
    if (!el || !document.contains(el)) return;
    const abilityId = el.dataset.ftAbilityId ?? '';
    if (abilityId !== '') (window as WeaponAbilitiesWindow).changeCharges?.(abilityId, el.id);
});

window.addEventListener('ft:charge-truth-applied', (event) => {
    const actorId = (event as CustomEvent<{ actorId?: string }>).detail?.actorId;
    if (actorId) rerenderActiveInfoPanel(actorId);
});

// Known class-mechanic UUID → variable-name icon key (verbatim from
// ft_class_mechanic_attack_header_suffix_html). Mechanics NOT in this map (e.g. menace/aether,
// which PHP resolves via ClassMechanic::getId) fall back to iconPh(uuid) — the registry keys the
// mechanic's own art by UUID — so every class mechanic still gets a sensible icon.
const CLASS_MECHANIC_ICON: Record<string, string> = {
    'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6': 'fury',
    '59F12E46-1F98-1352-1A13-BE202196C347': 'melody',
    'BD233002-F6C0-A136-6EE4-C899A9E92856': 'tactics',
    'C92D2BD6-DE2F-2A4D-7423-0C40CC8B3305': 'harmony',
    '8C9782A8-5EBF-C04F-CA3D-DF89B1C9EA18': 'stalk',
    '7926555F-184A-4E33-9630-A845D4C9335C': 'rites',
    '6A8183E1-349E-3960-B465-6C863097E193': 'psi',
    '2274AD74-E2F8-47AA-8FD1-7DE4713695B9': 'sanguine',
    '26A48978-7629-F0D3-BA6F-23DEBB429055': 'cunning',
    '30BD0A8F-DC53-4B80-B5C4-8FBB04F9F9C1': 'gifts',
    'B56929A7-6BB8-81FA-BFF8-2CE4B0812030': 'mastery',
    '5FF2F49A-7667-D686-2BC6-32623375A091': 'aether',   // spellblade
    'A8084E61-46B9-FDB5-83CD-59482BA5B8D3': 'menace',   // deathbringer
    '7A725B38-BA68-7651-4746-5D34B5690203': 'ether',    // summoner
    'DADEF475-1E69-4EF4-8B46-06F3FC2B450D': 'orb_slots', // machinist (queue strip, not a charge bar)
};

// Every class_mechanic UUID that renders a CHARGE bar (verbatim from additions.php ClassMechanic::LIST,
// EXCLUDING paladin's 'judgement' — it has no charge readout, mirroring class_mechanics_inc.php). Used to
// recognise a class mechanic among the active passive abilities directly (the reliable source — see
// activeClassMechanics), independent of whether it merged into the class_mechanic layer.
const CLASS_MECHANIC_BAR_IDS = new Set<string>([
    'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6', // fury
    '59F12E46-1F98-1352-1A13-BE202196C347', // melody
    'BD233002-F6C0-A136-6EE4-C899A9E92856', // tactics
    'C92D2BD6-DE2F-2A4D-7423-0C40CC8B3305', // harmony
    '8C9782A8-5EBF-C04F-CA3D-DF89B1C9EA18', // stalk
    '7926555F-184A-4E33-9630-A845D4C9335C', // rites
    '6A8183E1-349E-3960-B465-6C863097E193', // psi
    '2274AD74-E2F8-47AA-8FD1-7DE4713695B9', // sanguine
    '26A48978-7629-F0D3-BA6F-23DEBB429055', // cunning
    '30BD0A8F-DC53-4B80-B5C4-8FBB04F9F9C1', // gifts
    'B56929A7-6BB8-81FA-BFF8-2CE4B0812030', // mastery
    '5FF2F49A-7667-D686-2BC6-32623375A091', // aether
    'A8084E61-46B9-FDB5-83CD-59482BA5B8D3', // menace
    '7A725B38-BA68-7651-4746-5D34B5690203', // ether
]);

// ── CHARGE-LESS class mechanics (no current/max charge readout — shown differently) ──────────────
// IDs verbatim from additions.php (ClassMechanic::LIST / ::SHAMAN_MOTES; MonkStance is a status).
//   • Paladin JUDGEMENT — applied as a DEBUFF to OTHER creatures; the caster holds no charge, so we
//     show a static iconMap MARKER (click → ability info), never a 0/0 strip.
//   • Hunter STALK is deliberately NOT in this group: it is a real three-charge passive pool consumed
//     by Hunter abilities, so it belongs in chargedClassMechanics and must retain its current/max strip.
//   • Monk STANCE — the active stance is an exact MonkStance::LIST STATUS identity ON the monk.
//   • Shaman MOTES — four elemental-mote passive abilities, each its own charge/toggle (4-mote strip,
//     port of shaman_motes_bar_inc.php), not a single class-mechanic bar.
const JUDGEMENT_MECHANIC_ID = 'D98B076A-3A38-E225-B8E3-A646C69AA29D';
const MARKER_MECHANIC_IDS = new Set<string>([JUDGEMENT_MECHANIC_ID]);
const MARKER_MECHANIC_ICON: Record<string, string> = {
    [JUDGEMENT_MECHANIC_ID]: 'judgement',
};
// Judgement is not carried on the caster as a class_mechanic/passive entry (it is a debuff the
// caster's abilities apply to targets), so detect it by class membership — mirroring how
// class_mechanics_inc.php gates the mechanic per parent class. Class id from PlayableClass::LIST.
const PALADIN_CLASS_ID = 'D28986FA-DCD0-F760-9F58-9E0AEF386F38';
const MONK_CLASS_ID = '74D15961-05FC-1D0C-DDF6-F518F026F22B';
// MonkStance::LIST status identities (additions.php). Identity-bound so unrelated effects such as
// Strong Stance/Ashen Stance can never masquerade as the Monk's current class mechanic.
const MONK_STANCE_IDS = new Set<string>([
    'AB7AE7EE-C8DD-264F-6115-8B75F6B13CD1', // Huo
    '65D8750E-69AD-D583-0FB1-84B1E6476479', // Shu
    '212A0039-0A7C-436D-BC59-C87C41C5B567', // Yun
    'E9FC70FC-7F7C-48AB-B2AC-E25172104583', // Wenhe
    'F77D6A8D-D5F2-44ED-81DB-74ADCFF5B366', // Beijxing
    '2DB5900E-FF79-9BDA-ADA1-BE4DD50BCFE6', // Shizen
    'B2A59EE2-7D38-D406-0C34-51F1E59DEF6A', // Courtesan
    '0E37C3D2-CFA6-A1B2-F352-D2121868C425', // Spider
    'A3DCD286-9228-40C7-7ABF-3955559C3D56', // Dragon
    'AE49A833-AD4C-3715-6155-BEA0EBA374DC', // Drunken
    '3C0D5144-0BE8-6159-948D-E8E978AC4E19', // Puppet
    'B82D8A08-18AC-CB5A-91DA-CB7CDD03EE3C', // South
    'A922EFAA-2C01-88B9-6CF3-06D8C177AB05', // Pain
    'C3FB8547-44FE-7DA8-9601-DF2CBE44019A', // Blooming Fang
]);
const MARKER_MECHANIC_BY_CLASS: ReadonlyArray<{ classId: string; mechanicId: string }> = [
    { classId: PALADIN_CLASS_ID, mechanicId: JUDGEMENT_MECHANIC_ID },
];
const SHAMAN_MOTES: ReadonlyArray<{ abilityId: string; icon: string; name: string }> = [
    { abilityId: '3461E6F1-3EFF-8697-DB10-D4FEACF6679E', icon: 'air_mote', name: 'Air' },
    { abilityId: '3F3CB315-7406-30C9-D093-6BA50EF91739', icon: 'fire_mote', name: 'Fire' },
    { abilityId: 'F6D275CB-ACF3-DAC3-7236-810BC03A8298', icon: 'water_mote', name: 'Water' },
    { abilityId: '0B345CE4-611B-E8BA-F4A2-B15A43A0B193', icon: 'earth_mote', name: 'Earth' },
];

/** The class mechanics the caster currently HAS as an ACTIVE passive ability (the charge-bearing
 *  mechanics). Two deduped, order-preserving sources:
 *    1) class_mechanic layer entries that resolve to an active passive (the normal path), then
 *    2) PHP-parity fallback — any active passive that is itself a known class_mechanic id but did NOT merge
 *       into the class_mechanic layer. On a multiclass (e.g. priest/deathbringer Delion) Menace/Aether/Ether
 *       fall out of that merged layer, so the layer walk alone surfaced only Rites → the dock pivot never
 *       appeared. This mirrors the menace/aether/ether fallback blocks in class_mechanics_inc.php. */
function activeClassMechanics(caster: CachedCaster): Array<{ uuid: string; row: unknown }> {
    const activeById = new Map<string, unknown>();
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (passiveActive(pa)) {
            const pid = abilityEntryId(pa);
            if (pid) activeById.set(pid.toUpperCase(), pa);
        }
    }
    const mechs: Array<{ uuid: string; row: unknown }> = [];
    if (activeById.size === 0) return mechs;
    const seen = new Set<string>();
    const add = (uuid: string, row: unknown): void => {
        const U = String(uuid || '').toUpperCase();
        if (!U || !row || seen.has(U)) return;
        seen.add(U);
        mechs.push({ uuid: U, row });
    };
    // 1) class_mechanic layer entries (shell nests → UUID) that are an active passive.
    for (const cm of ftLayerEntries(caster, 'class_mechanic')) {
        const uuid = uuidOf(cm);
        if (uuid) add(uuid, activeById.get(uuid.toUpperCase()));
    }
    // 2) active-passive fallback for mechanics missing from the merged layer (menace/aether/ether multiclass).
    for (const [U, row] of activeById) {
        if (CLASS_MECHANIC_BAR_IDS.has(U)) add(U, row);
    }
    return mechs;
}

/** One mechanic charge strip: icon + −1 + current/max + +1. The ± buttons FLANK the count (red − on
 *  the left, blue + on the right) so the number sits between them — harder to misclick than the old
 *  count-then-both-buttons layout (owner direction). Buttons hidden when read-only. */
function mechanicStrip(mechanicId: string, passiveRow: unknown, ro: boolean): string {
    const current = num(abilityEntryCharges(passiveRow, 0), 0);
    const max = num(abilityEntryMaxCharges(passiveRow, 0), 0);
    const iconKey = CLASS_MECHANIC_ICON[mechanicId] || mechanicId;
    const decBtn = ro ? '' :
        `<button type="button" class="btn btn-danger btn-sm py-0 px-1 ft-class-mechanic-attacks-strip__chg" ${weaponAbilityAttrs('adjust-charge', { abilityId: mechanicId, delta: '-1' })}>${iconPh('decrease_charge')}</button>`;
    const incBtn = ro ? '' :
        `<button type="button" class="btn btn-primary btn-sm py-0 px-1 ft-class-mechanic-attacks-strip__chg" ${weaponAbilityAttrs('adjust-charge', { abilityId: mechanicId, delta: '1' })}>${iconPh('increase_charge')}</button>`;
    return `<div class="d-flex align-items-center gap-1 flex-nowrap ft-class-mechanic-attacks-strip">`
        + `<div class="flex-shrink-0">${iconPh(iconKey)}</div>`
        + decBtn
        + `<span class="badge bg-dark border border-light text-white fs-6 fw-bold text-nowrap">${current} / ${max}</span>`
        + incBtn
        + `</div>`;
}

/** Mechanics that actually use the CHARGE system (max capacity > 0). Paladin's Judgement and the
 *  Shaman elemental nodes are debuff/resource mechanics with NO self-charge — they
 *  reach the class_mechanic layer (so the layer-walk in activeClassMechanics surfaces them) but a
 *  charge readout for them is meaningless and rendered a bogus "0 / 0" strip. Gate the charge UI on
 *  real capacity so only charge-driven mechanics (fury/melody/psi/Stalk/…) show a current/max strip;
 *  charge-less ones get NO strip here (their state surfaces elsewhere — e.g. Monk's stance status). */
function chargedClassMechanics(caster: CachedCaster): Array<{ uuid: string; row: unknown }> {
    return activeClassMechanics(caster).filter((m) =>
        !MARKER_MECHANIC_IDS.has(m.uuid.toUpperCase())
        // The Machinist's Orb Slots pool DOES declare a charge max (= slot capacity), so it would
        // pass the >0 gate and paint a "3 / 3" bar meaning "3 slots", which every player reads as
        // "3 orbs held". It is rendered by orbQueueHtml() instead — see the ORB QUEUE block below.
        && m.uuid.toUpperCase() !== ORB_SLOTS_MECHANIC_ID
        && num(abilityEntryMaxCharges(m.row, 0), 0) > 0);
}

// ── charge-less mechanic indicators (markers / monk stance / shaman motes) ───────────────────────

/** True when the caster (a PC) has the given class in any class_1..20 slot. Mirrors
 *  casterClassLevelMap's class read (uuidOf tolerates the bare-UUID / provider-nest slot shapes). */
function casterHasClassId(caster: CachedCaster, classId: string): boolean {
    const pc = caster?.player_characters;
    if (!pc || typeof pc !== 'object') return false;
    const want = classId.toUpperCase();
    for (let x = 1; x <= 20; x++) {
        const cls = pc['class_' + x];
        if (cls === undefined || cls === null || cls === '') continue;
        if (String(uuidOf(cls)).toUpperCase() === want) return true;
    }
    return false;
}

const MARKER_MECHANIC_NAME: Record<string, string> = {
    [JUDGEMENT_MECHANIC_ID]: 'Judgement',
};

/** Static icon marker for Judgement (a debuff the caster's abilities apply to OTHER creatures — no
 *  self-charge). Detected by class membership (Paladin → Judgement). iconMap art keyed by the
 *  mechanic's variable name, click → the mechanic's info. NO current/max readout. */
function mechanicMarkerHtml(caster: CachedCaster): string {
    let html = '';
    for (const { classId, mechanicId } of MARKER_MECHANIC_BY_CLASS) {
        if (!casterHasClassId(caster, classId)) continue;
        const name = MARKER_MECHANIC_NAME[mechanicId] || '';
        const iconKey = MARKER_MECHANIC_ICON[mechanicId] || mechanicId;
        html += `<span class="ft-class-mechanic-marker d-inline-flex align-items-center flex-shrink-0" role="button"`
            + ` ${weaponAbilityInfoAttrs(mechanicId)}>${iconMap(iconKey, 22, 22, name)}</span>`;
    }
    return html;
}

/** The Monk's active MonkStance::LIST status applied TO the caster. */
function activeMonkStance(caster: CachedCaster): { id: string; name: string } | null {
    // PCs must actually own the Monk class. Non-PCs have no class slots: for them an exact
    // MonkStance::LIST status is explicit creature authoring, like an NPC carrying Ether/Fury.
    const pc = caster?.player_characters;
    if (pc && typeof pc === 'object' && !casterHasClassId(caster, MONK_CLASS_ID)) return null;
    if (typeof ftCreatureStatuses !== 'function') return null;
    let rows: unknown[] = [];
    try { rows = ftCreatureStatuses(caster, 'target') || []; } catch { rows = []; }
    for (const row of rows) {
        const rowRecord = row && typeof row === 'object' && !Array.isArray(row) ? row as Record<string, unknown> : null;
        const def = rowRecord?.status_arr && typeof rowRecord.status_arr === 'object' ? rowRecord.status_arr : row;
        const id = String(ftItemsCol(def, 'item_id') ?? '').toUpperCase();
        if (!MONK_STANCE_IDS.has(id)) continue;
        // HTML-encoded in the DB — decode before the escapeHtml in monkStanceHtml (see ability-row).
        const name = decodeHtmlEntitiesForPlainText(String(ftItemsCol(def, 'item_name') ?? ''));
        return { id, name };
    }
    return null;
}

/** Monk active-stance chip: stance icon (+ name badge in the banner). The action-bar dock shows just
 *  the ICON of the stance we're in (owner direction); the banner adds the name. '' when no stance. */
function monkStanceHtml(caster: CachedCaster, compact = false): string {
    const st = activeMonkStance(caster);
    if (!st) return '';
    const name = escapeHtml(st.name);
    const idAttr = escapeHtml(st.id);
    const click = st.id ? ` role="button" ${weaponAbilityInfoAttrs(st.id)}` : '';
    const icon = st.id ? iconMap(st.id, compact ? 18 : 22, compact ? 18 : 22) : '';
    const label = compact ? '' : `<span class="badge bg-dark border border-light text-white text-nowrap">${name}</span>`;
    return `<span class="ft-class-mechanic-stance d-inline-flex align-items-center gap-1 flex-shrink-0"${click}`
        + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${name}">${icon}${label}</span>`;
}

/** Shaman elemental-mote strip (port of shaman_motes_bar_inc.php): four mote passives, each its own
 *  icon + current/max + toggle. Gated on the caster actually carrying ≥1 mote passive (only shamans
 *  do). `compact` drops the heavy padding/border for the inline action-bar dock. */
function shamanMotesHtml(caster: CachedCaster, ro: boolean, compact = false): string {
    const byId = new Map<string, unknown>();
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (!passiveActive(pa)) continue;
        const id = String(abilityEntryId(pa) || '').toUpperCase();
        if (id) byId.set(id, pa);
    }
    if (!SHAMAN_MOTES.some((m) => byId.has(m.abilityId.toUpperCase()))) return '';
    let slots = '';
    for (const mote of SHAMAN_MOTES) {
        const row = byId.get(mote.abilityId.toUpperCase()) ?? null;
        const current = num(abilityEntryCharges(row, 0), 0);
        let max = num(abilityEntryMaxCharges(row, 1), 1);
        if (max < 1) max = 1;
        const active = current > 0;
        const interactive = row !== null;
        const canToggle = interactive && !ro;
        let cls = 'ft-shaman-mote d-flex flex-column align-items-center justify-content-center rounded border ' + (compact ? 'p-1' : 'p-2');
        cls += active ? ' ft-shaman-mote--active' : ' ft-shaman-mote--empty';
        if (!interactive) cls += ' ft-shaman-mote--locked';
        if (interactive && ro) cls += ' ft-shaman-mote--view-only';
        const title = !interactive ? 'Mote not available' : (ro ? 'Mote (view only)' : 'Toggle mote charges');
        const interaction = canToggle
            ? `role="button" tabindex="0" ${weaponAbilityAttrs('shaman-mote-toggle', { abilityId: mote.abilityId, current: String(current), max: String(max) })}`
            : 'aria-disabled="true"';
        slots += `<div class="${cls}" ${interaction} title="${title}">`
            + `<span class="ft-shaman-mote-icon">${iconPh(mote.icon, compact ? 18 : 25, compact ? 18 : 25)}</span>`
            + (max > 1 ? `<span class="small text-muted ft-shaman-mote-count">${current} / ${max}</span>` : '')
            + `</div>`;
    }
    const wrap = compact
        ? 'd-inline-flex align-items-stretch gap-1 ft-shaman-motes-bar ft-shaman-motes-bar--compact'
        : 'd-inline-flex align-items-stretch flex-wrap gap-2 ft-shaman-motes-bar';
    return `<div class="${wrap}" role="group" aria-label="Shaman motes">${slots}</div>`;
}

// ── MACHINIST ORB QUEUE ──────────────────────────────────────────────────────────────────────
// Orb Slots is a QUEUE mechanic, not a charge mechanic: ORDER is the rule (queue index 0 = oldest
// = next to evoke), and one "3 / 3" charge integer cannot carry it. Worse, the pool's `current` is
// slot CAPACITY, not orbs held — a generic charge strip reads as "3 orbs channeled" and is simply
// wrong. So Orb Slots is excluded from chargedClassMechanics() (ORB_SLOTS_MECHANIC_ID below) and
// gets this dedicated strip instead.
//
// LAYOUT MIRRORS THE TOKEN ARC (creature-render/creature-thumbnail.ts buildOrbArcHTML) so the two
// readouts can never disagree: next-to-evoke sits RIGHTMOST, newly channeled orbs enter from the
// left, and the empty slots hold the left end — exactly where the next orb lands. Colors are that
// arc's palette verbatim; slugs are OrbType::LIST variable_names (additions.php).
const ORB_SLOTS_MECHANIC_ID = 'DADEF475-1E69-4EF4-8B46-06F3FC2B450D';
/** StS baseline capacity — mirrors FT_ORB_QUEUE_DEFAULT_SLOTS (janus/encounter/orb_queue.php). */
const ORB_QUEUE_DEFAULT_SLOTS = 3;
const ORB_QUEUE_COLORS: Record<string, string> = {
    lightning: '#f5d90a',
    cold:      '#6ec6ff',
    necrotic:  '#8b2fd6',
    thunder:   '#4757d6',
    fire:      '#ff7a1a',
    force:     '#c08cff',
    poison:    '#35c235',
    acid:      '#9acd32',
};

/** The caster's ACTIVE Orb Slots passive row, or null. Capability-based (the active passive), NOT
 *  class membership — same rule the charge mechanics use, so a multiclass Machinist still gets the
 *  strip. Note the Machinist CLASS item carries no `class_mechanic` layer, so the layer walk in
 *  activeClassMechanics() can never surface Orb Slots; this reads the passive directly. */
function orbSlotsPassive(caster: CachedCaster): unknown {
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (!passiveActive(pa)) continue;
        if (String(abilityEntryId(pa) || '').toUpperCase() === ORB_SLOTS_MECHANIC_ID) return pa;
    }
    return null;
}

/** Channeled orbs, OLDEST FIRST — the decoded slug list off the compiled EC bucket
 *  (encounter_creatures.orb_queue). Unknown/non-string entries are dropped defensively, matching
 *  ft_normalize_orb_queue() and buildOrbArcHTML. hasOwnProperty.call, not `in`: `in` accepts
 *  inherited keys ('constructor', '__proto__'), which breaks the whitelist boundary under prototype
 *  pollution. (buildOrbArcHTML spells it the same way since 2026-08-22 — it had used Object.hasOwn,
 *  which is ES2022 and failed typecheck against this project's ES2020 lib.) */
function orbQueueOf(caster: CachedCaster): string[] {
    const raw = caster?.encounter_creatures?.orb_queue;
    if (!Array.isArray(raw)) return [];
    return raw.filter((s): s is string =>
        typeof s === 'string' && Object.prototype.hasOwnProperty.call(ORB_QUEUE_COLORS, s));
}

/** Slot capacity: the Orb Slots pool's CURRENT charges (Capacitor raises this combat-scoped value),
 *  falling back to its authored max and then the StS baseline. FLOORED before clamping — charge
 *  values can arrive as non-integer strings. Bounded at 10 to mirror FT_ORB_QUEUE_MAX_SLOTS. */
function orbSlotCapacity(passiveRow: unknown): number {
    const current = Math.floor(num(abilityEntryCharges(passiveRow, 0), 0));
    const maximum = Math.floor(num(abilityEntryMaxCharges(passiveRow, 0), 0));
    const declared = current > 0 ? current : maximum;
    return Math.max(1, Math.min(10, declared > 0 ? declared : ORB_QUEUE_DEFAULT_SLOTS));
}

/** One pip. `slug` empty ⇒ an unfilled slot. */
function orbPipHtml(slug: string, px: number, isNext: boolean): string {
    const common = `display:inline-block;width:${px}px;height:${px}px;border-radius:50%;flex-shrink:0;`;
    if (slug === '') {
        return `<span class="ft-orb-pip ft-orb-pip--empty" title="Empty orb slot" aria-label="Empty orb slot"`
            + ` style="${common}border:1px dashed rgba(255,255,255,.35);background:rgba(255,255,255,.05);"></span>`;
    }
    const color = ORB_QUEUE_COLORS[slug] ?? '#ffffff';
    const name = `${slug.charAt(0).toUpperCase()}${slug.slice(1)} Orb${isNext ? ' — next to evoke' : ''}`;
    return `<span class="ft-orb-pip${isNext ? ' ft-orb-pip--next' : ''}" title="${escapeHtml(name)}" aria-label="${escapeHtml(name)}"`
        + ` style="${common}background:radial-gradient(circle at 35% 30%, #ffffffcc 0%, ${color} 45%, ${color}33 100%);`
        + `box-shadow:0 0 ${isNext ? 8 : 4}px 1px ${color}${isNext ? 'ee' : '88'};`
        + `${isNext ? `outline:2px solid ${color};outline-offset:2px;` : ''}"></span>`;
}

/** The Machinist orb-queue strip: `capacity` pips, filled in queue order with the next-to-evoke orb
 *  rightmost and marked, plus a "held / capacity" count so the NUMBER is readable at a glance.
 *  Inside combat it renders even for an EMPTY queue, because the empty slots are themselves the
 *  information (how many orbs can still be channeled). `compact` drops the label + padding for the
 *  action-bar dock. '' for every non-Machinist.
 *
 *  COMBAT-ONLY (owner 2026-08-09). Orbs are combat-scoped state — ft_clear_orb_queue() drops the
 *  whole queue on combat exit and on death — so out of combat the strip could only ever show N
 *  empty sockets, which reads as a broken/irrelevant widget on the sheet of a Machinist standing in
 *  a town. Gated on the ENCOUNTER's in_combat, not the creature's (see casterIsInCombat). */
export function orbQueueHtml(caster: CachedCaster, compact = false): string {
    const row = orbSlotsPassive(caster);
    if (!row) return '';
    if (!casterIsInCombat(caster)) return '';
    const capacity = orbSlotCapacity(row);
    const queue = orbQueueOf(caster).slice(0, capacity);
    const px = compact ? 13 : 20;

    // Display order = queue REVERSED (index 0 rightmost), so the empty slots lead.
    let pips = '';
    for (let i = queue.length; i < capacity; i++) pips += orbPipHtml('', px, false);
    for (let i = queue.length - 1; i >= 0; i--) pips += orbPipHtml(queue[i] as string, px, i === 0);

    const count = `<span class="badge bg-dark border border-light text-white text-nowrap${compact ? ' small' : ''}">`
        + `${queue.length} / ${capacity}</span>`;
    const wrapCls = 'ft-orb-queue-strip d-inline-flex align-items-center flex-nowrap'
        + (compact ? ' gap-1' : ' gap-2');
    return `<div class="${wrapCls}" role="group" aria-label="Orb queue"`
        + ` data-bs-toggle="tooltip" data-bs-placement="top"`
        + ` data-bs-original-title="Orbs evoke oldest first — the highlighted orb is next">`
        + `<span class="flex-shrink-0">${iconPh('orb_slots', compact ? 18 : 25, compact ? 18 : 25)}</span>`
        + `<span class="d-inline-flex align-items-center flex-nowrap" style="gap:${compact ? 3 : 5}px;">${pips}</span>`
        + count
        + `</div>`;
}

/** The Abilities tab's CLASS MECHANICS section host (data-ft-pc-pane="orb_queue"), mounted by
 *  class_mechanics_inc.php next to the charge bars. Rendered as a PANE rather than in PHP because
 *  the queue reorders on every channel/evoke: a page-load-time PHP render would be stale the moment
 *  the Machinist acts, whereas a pane repaints surgically on caster_update. '' for non-Machinists,
 *  which leaves the mount div inert. */
export function renderOrbQueueSection(caster: CachedCaster): string {
    const strip = orbQueueHtml(caster, false);
    if (strip === '') return '';
    return `<div class="d-flex justify-content-center align-items-center p-3 m-3 border rounded ft-class-mechanic-bar ft-orb-queue-bar">`
        + strip
        + `</div>`;
}

/** Charge-LESS mechanic indicators shown in BOTH the banner and the action-bar dock: Monk stance,
 *  Shaman motes, and the Machinist orb queue. The Judgement MARKER is deliberately NOT here — it
 *  rides the banner only (mechanicMarkerHtml, added directly in classMechanicBanner); the owner
 *  doesn't want it cluttering the dock's pivot row. Empty for a caster with none of them. */
function nonChargeMechanicHtml(caster: CachedCaster, ro: boolean, compact = false): string {
    return monkStanceHtml(caster, compact) + shamanMotesHtml(caster, ro, compact) + orbQueueHtml(caster, compact);
}

/** Class-mechanic charge banner for the attack-abilities header. Shows a strip for each class
 *  mechanic the caster has that is ALSO an active passive ability WITH a real charge capacity. */
export function classMechanicBanner(caster: CachedCaster, ro: boolean): string {
    // Each distinct group (charge strips · Judgement marker · stance+motes) is separated by a
    // thin vertical divider, and the whole cluster LEADS with one — so it reads as its own block apart
    // from the "Attack Abilities" title instead of crowding it (owner: "looks a little strange").
    const groups: string[] = [];
    const charge = chargedClassMechanics(caster).map((m) => mechanicStrip(m.uuid, m.row, ro)).join('');
    if (charge) groups.push(charge);
    const marker = mechanicMarkerHtml(caster);   // Judgement marker — BANNER only (not the dock)
    if (marker) groups.push(marker);
    const other = nonChargeMechanicHtml(caster, ro, false);
    if (other) groups.push(other);
    if (groups.length === 0) return '';
    const divider = '<span aria-hidden="true" class="ft-mech-divider"'
        + ' style="align-self:stretch;flex-shrink:0;width:1px;min-height:1.6rem;margin:0 .15rem;background:rgba(255,255,255,.28);"></span>';
    return divider + groups.join(divider);
}

/** Bar-style class-mechanic charge readout for the action-bar dock — matches the HP/Mana bars: a slim
 *  filled track (current/max charges) per mechanic, flanked by tiny −/+ when editable. Same `.ft-action-res-*`
 *  markup the dock HP/Mana bars use (styled in action-bar.ts). */
function mechanicBar(mechanicId: string, passiveRow: unknown, ro: boolean, casterId: string, pivot = ''): string {
    const current = num(abilityEntryCharges(passiveRow, 0), 0);
    const max = num(abilityEntryMaxCharges(passiveRow, 0), 0);
    const pct = max > 0 ? Math.max(0, Math.min(100, (current / max) * 100)) : 0;
    const iconKey = CLASS_MECHANIC_ICON[mechanicId] || mechanicId;
    const id = escapeHtml(mechanicId);
    const cid = escapeHtml(casterId);
    const name = escapeHtml(abilityEntryName(passiveRow) || '');
    // No inline ±/charge buttons on the bar anymore — they were tiny + confusing. Clicking the TRACK opens
    // the mechanic side-info panel (like HP/Mana), whose slider sets the charges (owner direction 2026-06-26).
    return `<div class="ft-action-res-bar ft-action-res-bar--mech" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${name}">`
        + `${pivot}<span class="ft-action-res-ico">${iconPh(iconKey, 16, 16)}</span>`
        + `<span class="ft-action-res-track" role="button" ${weaponAbilityAttrs('class-mechanic-info', { casterId, mechanicId })}>`
        + `<span class="ft-action-res-fill ft-action-res-fill--mech" style="width:${pct}%"></span>`
        + `<span class="ft-action-res-label">${current} / ${max}</span></span></div>`;
}

/** Class mechanics as a bar-style dock readout. ONE bar; with 2+ mechanics (rare — e.g. a multiclass
 *  with two mechanics) it shows the one at `activeIdx` plus a ▲/▼ carrot pivot to cycle (state + repaint
 *  live in action-bar.ts via ftActionBarPivotMechanic). Same mechanic selection as classMechanicBanner. */
export function classMechanicBars(caster: CachedCaster, ro: boolean, casterId = '', activeIdx = 0): string {
    // Charge-less indicators (Monk stance and Shaman motes) ride after the charge
    // bar in the dock — compact form. They render even when the caster has NO charge-bar mechanic.
    const extra = nonChargeMechanicHtml(caster, ro, true);
    const extraWrapped = extra ? `<div class="ft-action-res-mechx d-inline-flex align-items-center gap-2">${extra}</div>` : '';

    const mechs = chargedClassMechanics(caster);
    if (mechs.length === 0) return extraWrapped;
    const first = mechs[0];
    if (!first) return extraWrapped;
    if (mechs.length === 1) return mechanicBar(first.uuid, first.row, ro, casterId) + extraWrapped;

    const count = mechs.length;
    const idx = ((Number(activeIdx) % count) + count) % count; // wrap
    const cid = escapeHtml(casterId);
    const pivot = `<span class="ft-action-mech-pivot" aria-label="Switch class mechanic (${idx + 1}/${count})">`
        + `<button type="button" class="ft-action-mech-carrot" ${weaponAbilityAttrs('pivot-mechanic', { casterId, delta: '-1' })} aria-label="Previous">&#9650;</button>`
        + `<button type="button" class="ft-action-mech-carrot" ${weaponAbilityAttrs('pivot-mechanic', { casterId, delta: '1' })} aria-label="Next">&#9660;</button>`
        + `</span>`;
    const active = mechs[idx] ?? first;
    return mechanicBar(active.uuid, active.row, ro, casterId, pivot) + extraWrapped;
}

/** Party-frame projection: preserve charge bars, but turn Shaman's four-slot control into readable
 * active-mode badges and omit Monk stance (the party roster is state-at-a-glance, not a second sheet).
 * The action bar keeps its complete interactive/compact mechanic treatment. */
function partyShamanModeBadges(caster: CachedCaster): string {
    const byId = new Map<string, unknown>();
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (!passiveActive(pa)) continue;
        const id = String(abilityEntryId(pa) || '').toUpperCase();
        if (id) byId.set(id, pa);
    }
    if (!SHAMAN_MOTES.some((mote) => byId.has(mote.abilityId.toUpperCase()))) return '';
    const badges = SHAMAN_MOTES.map((mote) => {
        const row = byId.get(mote.abilityId.toUpperCase());
        const current = num(abilityEntryCharges(row, 0), 0);
        const max = Math.max(1, num(abilityEntryMaxCharges(row, 1), 1));
        const active = current > 0;
        const name = escapeHtml(mote.name);
        const state = active ? 'available' : 'depleted';
        const summary = `${name} mote: ${current} / ${max} (${state})`;
        return `<span class="ft3d-party-shaman-mode ${active ? 'is-active' : 'is-depleted'}"`
            + ` data-ft3d-shaman-mode="${name.toLowerCase()}" aria-label="${summary}" title="${summary}">`
            + `<span class="ft3d-party-shaman-mode__icon">${iconPh(mote.icon, 16, 16)}</span></span>`;
    }).join('');
    return `<span class="ft3d-party-shaman-modes" role="group" aria-label="Shaman motes">${badges}</span>`;
}

export function partyClassMechanicBars(caster: CachedCaster, casterId = ''): string {
    const extra = partyShamanModeBadges(caster) + orbQueueHtml(caster, true);
    const extraWrapped = extra
        ? `<div class="ft-action-res-mechx ft-action-res-mechx--party d-inline-flex align-items-center gap-2">${extra}</div>`
        : '';
    const first = chargedClassMechanics(caster)[0];
    return (first ? mechanicBar(first.uuid, first.row, true, casterId) : '') + extraWrapped;
}

/** Scalar dependencies of the party readout: one charge bar, four motes and at most ten orbs. */
export function partyClassMechanicResourceKey(caster: CachedCaster): string {
    const first = chargedClassMechanics(caster)[0];
    const passives = new Map(abilityEntriesOfType(caster, 'passive_ability').filter(passiveActive)
        .map(row => [String(abilityEntryId(row) || '').toUpperCase(), row]));
    const orb = orbSlotsPassive(caster);
    const capacity = orb ? orbSlotCapacity(orb) : 0;
    return JSON.stringify([
        first ? [first.uuid, abilityEntryName(first.row), num(abilityEntryCharges(first.row, 0), 0), num(abilityEntryMaxCharges(first.row, 0), 0)] : null,
        SHAMAN_MOTES.map(mote => {
            const row = passives.get(mote.abilityId.toUpperCase());
            return [row !== undefined, num(abilityEntryCharges(row, 0), 0), Math.max(1, num(abilityEntryMaxCharges(row, 1), 1))];
        }),
        orb && casterIsInCombat(caster) ? [capacity, orbQueueOf(caster).slice(0, capacity)] : null,
    ]);
}
Object.assign(window, { ftPartyClassMechanicResourceKey: partyClassMechanicResourceKey });

/** Side-info panel for a class mechanic — opened by clicking its dock bar (mirrors the HP/Mana panels):
 *  current/max charges + an absolute charge setter. Registered with setActiveInfoPanel so it diffs
 *  the held actor's charge changes, including FT3D actors absent from the caster cache. The
 *  max-charge SOURCE breakdown (feats) is a TODO — needs the charge-computation contributors. */
export function renderClassMechanicInfoPanel(casterId: string, mechanicId: string, targetId = 'div_refresh_side_info'): void {
    const target = document.getElementById(targetId);
    if (!target) return;
    const heldCaster = (): CachedCaster | undefined => (window as WeaponAbilitiesWindow).ftChargeActorRow?.(casterId)
        ?? window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    const render = (caster: CachedCaster): string => {
        const want = String(mechanicId).toUpperCase();
        const row = abilityEntriesOfType(caster, 'passive_ability').find((p: unknown) => String(abilityEntryId(p) || '').toUpperCase() === want);
        if (!row) return '<div class="p-3 text-center text-muted">Class mechanic not found.</div>';
        const name = escapeHtml(abilityEntryName(row) || 'Class Mechanic');
        const current = num(abilityEntryCharges(row, 0), 0);
        const max = num(abilityEntryMaxCharges(row, 0), 0);
        const iconKey = CLASS_MECHANIC_ICON[want] || mechanicId;
        const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
        let html = `<table class="table border"><thead><tr><th colspan="2" class="table-dark align-middle rounded-top">`
            + `<h4 class="text-white">${iconPh(iconKey, 24, 24)} ${name}</h4></th></tr></thead><tbody>`
            + `<tr><th scope="row" class="align-middle">Current:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-warning text-dark">${current}</span></h3></td></tr>`
            + `<tr><th scope="row" class="align-middle">Max:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-dark border text-white">${max}</span></h3></td></tr>`
            + `</tbody></table>`;
        if (canEdit && max > 0) {
            const sliderVal = Math.min(Math.max(0, current), max);
            html += `<div class="p-2 border rounded mb-2"><h5 class="text-center mb-2">Set charges</h5>`
                + `<div class="d-flex align-items-center px-1"><span class="small text-muted mr-2">0</span>`
                + `<input id="ft_mech_charge_slider" type="range" class="form-range flex-grow-1 mx-1" min="0" max="${max}" step="1" value="${sliderVal}" aria-label="Set charges" ${weaponAbilityAttrs('mechanic-slider', { abilityId: mechanicId, labelId: 'ft_mech_slider_lbl' })}>`
                + `<span class="small text-muted ml-2">${max}</span></div>`
                + `<div class="text-center small text-muted" id="ft_mech_slider_lbl">${sliderVal}</div>`
                + `<p class="small text-muted text-center mb-0">Drag to set current charges.</p></div>`;
        }
        // TODO source breakdown: which feats/passives raise this mechanic's MAX charges (needs the
        // charge-computation contributor list) — show them here once available.
        return html;
    };
    setActiveInfoPanel(casterId, targetId, row => render(row as unknown as CachedCaster),
        () => heldCaster() as unknown as InfoPanelCaster | undefined);
    const caster = heldCaster();
    if (caster) paintActiveInfoPanel(caster as unknown as InfoPanelCaster);
    else target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
}

/** Render the attack + opportunity-attack ability tables from the caster blob. */
export function renderWeaponAbilities(caster: CachedCaster, _casterId: string, isStatic = false): string {
    // isStatic (item-creature statblock): force read-only — attack abilities are reference-only here.
    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const banner = classMechanicBanner(caster, !canEdit);
    const attack = buildSection(caster, 'attack_ability', 'danger', canEdit, { headerSuffixHtml: banner });
    const opportunity = buildSection(caster, 'opportunity_attack_ability', 'dark', canEdit);
    if (attack === '' && opportunity === '') return '';
    return `<div class="ft-pc-abilities ft-pc-weapon-abilities">${attack}${opportunity}</div>`;
}
